import React, { useState } from 'react';
import {
  MessageSquareText,
  AlertTriangle,
  Lightbulb,
  Bug,
  ArrowRight,
  Clock,
  User,
  ChevronDown,
  ChevronRight,
  Code2,
  RefreshCcw,
  CheckCircle2,
  FileText,
} from 'lucide-react';
import { SeverityBadge, FeedbackStatusBadge } from '../components/shared/StatusBadge';
import { useApp } from '../context/AppContext';

const typeIcons: Record<string, React.FC<{ size?: number; className?: string }>> = {
  edge_case: (props) => <Bug {...props} />,
  policy_violation: (props) => <AlertTriangle {...props} />,
  optimization: (props) => <Lightbulb {...props} />,
};

const typeColors: Record<string, { bg: string; text: string }> = {
  edge_case: { bg: 'bg-amber-50', text: 'text-amber-600' },
  policy_violation: { bg: 'bg-red-50', text: 'text-red-600' },
  optimization: { bg: 'bg-blue-50', text: 'text-blue-600' },
};

export const FeedbackLoop: React.FC = () => {
  const { feedbackItems, applyAction, dismissFeedback, updateFeedbackStatus, navigateToDocument } = useApp();
  const [expandedItem, setExpandedItem] = useState<string | null>(feedbackItems[0]?.id ?? null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'open' | 'critical'>('all');

  const displayItems = feedbackItems.filter(item => {
    if (activeFilter === 'open') return item.status === 'open';
    if (activeFilter === 'critical') return item.severity === 'critical';
    return true;
  });

  const stats = {
    total: feedbackItems.length,
    open: feedbackItems.filter(f => f.status === 'open').length,
    inReview: feedbackItems.filter(f => f.status === 'in_review').length,
    resolved: feedbackItems.filter(f => f.status === 'resolved').length,
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="glass-card p-5 animate-slide-up">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Total Items</p>
              <p className="metric-value mt-2">{stats.total}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <MessageSquareText size={20} className="text-brand-400" />
            </div>
          </div>
        </div>
        <div className="glass-card p-5 animate-slide-up">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Open</p>
              <p className="metric-value mt-2">{stats.open}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Clock size={20} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="glass-card p-5 animate-slide-up">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">In Review</p>
              <p className="metric-value mt-2">{stats.inReview}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <RefreshCcw size={20} className="text-amber-600" />
            </div>
          </div>
        </div>
        <div className="glass-card p-5 animate-slide-up">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Resolved</p>
              <p className="metric-value mt-2">{stats.resolved}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 size={20} className="text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Items */}
      <div className="glass-card animate-slide-up">
        <div className="px-5 pt-5 pb-3 border-b border-surface-100 flex items-center justify-between">
          <div>
            <h3 className="section-title">Feedback Queue</h3>
            <p className="section-subtitle">TRACE AI model improvement suggestions & compliance flags</p>
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'open', 'critical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors capitalize ${
                  activeFilter === f
                    ? 'text-brand-400 bg-brand-50'
                    : 'text-surface-500 hover:bg-surface-100'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-surface-50">
          {displayItems.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-surface-400">
              <p className="text-sm">No feedback items match this filter</p>
            </div>
          ) : (
            displayItems.map((item) => {
              const isExpanded = expandedItem === item.id;
              const TypeIcon = typeIcons[item.type] || ((props: any) => <Bug {...props} />);
              const typeColor = typeColors[item.type] || typeColors.edge_case;

              return (
                <div key={item.id} className={`transition-colors ${isExpanded ? 'bg-surface-50/50' : ''}`}>
                  <button
                    onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                    className="w-full text-left px-5 py-4 flex items-start gap-4"
                  >
                    {/* Type Icon */}
                    <div className={`w-9 h-9 rounded-lg ${typeColor.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <TypeIcon size={16} className={typeColor.text} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-surface-800">{item.title}</span>
                        <SeverityBadge severity={item.severity as any} />
                        <FeedbackStatusBadge status={item.status} />
                      </div>
                      <p className="text-xs text-surface-500 line-clamp-1">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-[11px] text-surface-400">
                        <span className="flex items-center gap-1">
                          <FileText size={11} /> {item.documentId}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={11} /> {item.assignee}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Expand Arrow */}
                    <div className="mt-1 text-surface-400">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pl-[72px] animate-fade-in">
                      <div className="space-y-4">
                        {/* Description */}
                        <div>
                          <p className="text-xs font-semibold text-surface-600 mb-1.5">Description</p>
                          <p className="text-sm text-surface-600 leading-relaxed">{item.description}</p>
                        </div>

                        {/* Status Changer */}
                        {item.status !== 'resolved' && (
                          <div>
                            <p className="text-xs font-semibold text-surface-600 mb-1.5">Update Status</p>
                            <div className="flex items-center gap-2">
                              {item.status !== 'in_review' && (
                                <button
                                  onClick={() => updateFeedbackStatus(item.id, 'in_review')}
                                  className="text-xs font-medium text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors"
                                >
                                  Move to Review
                                </button>
                              )}
                              <button
                                onClick={() => updateFeedbackStatus(item.id, 'resolved')}
                                className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors"
                              >
                                Mark Resolved
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Proposed Action */}
                        <div>
                          <p className="text-xs font-semibold text-surface-600 mb-1.5 flex items-center gap-1.5">
                            <Code2 size={12} />
                            Proposed Action
                          </p>
                          <div className="bg-surface-900 rounded-lg p-4">
                            <pre className="text-xs font-mono text-emerald-400 whitespace-pre-wrap leading-relaxed">
                              {item.proposedAction}
                            </pre>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => applyAction(item.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-400 rounded-lg hover:bg-brand-500 transition-colors"
                          >
                            Apply Action <ArrowRight size={12} />
                          </button>
                          <button
                            onClick={() => navigateToDocument(item.documentId)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-surface-600 bg-surface-100 rounded-lg hover:bg-surface-200 transition-colors"
                          >
                            View Document
                          </button>
                          <button
                            onClick={() => dismissFeedback(item.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
